import { BffApiClientMolecule } from "@/app/api-client/store"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { TensorOfferRef } from "@anaconda/core/bid/types"
import { Blockchain } from "@anaconda/core/blockchain/types"
import type { Nft } from "@anaconda/core/nft/types"
import { Trans } from "@lingui/react/macro"
import { SolanaTokens } from "@rarible_int/anaconda_sdk/solana"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import { molecule } from "bunshi"
import { SolanaSdkContainerMoleculeReatom } from "../../blockchains/solana-sdk-container-molecule.reatom"
import { AppToast, SuccessViewTransaction } from "../../toasts/app-toast"
import { getTransactionErrorMessage } from "../events/transaction-event"
import { invalidateNftOffersQueries } from "../order/invalidate-nft-offers-queries"
import {
  readExternalTradeError,
  sendExternalSolanaTransaction,
} from "./send-external-solana-transaction"

export interface SolanaAcceptTensorOfferParams {
  nft: Nft
  offer: TensorOfferRef
}

/**
 * Accepting a Tensor offer — a single-NFT bid, a collection bid or selling
 * into an AMM pool. Tensor fills all three through one builder; the BFF
 * re-reads the bid or pool, refuses if it now pays less than the row showed,
 * and hands back a transaction only the seller signs.
 */
export const SolanaAcceptTensorOfferMoleculeReatom = molecule((mol) => {
  const { sdkContainerAtom } = mol(SolanaSdkContainerMoleculeReatom)
  const bffApiClient = mol(BffApiClientMolecule)
  const { solanaAddress: solanaAddressAtom } = mol(WalletConnectMoleculeReatom)

  const acceptTensorOfferActionFactory = () => {
    const acceptAction = action(
      async ({ nft, offer }: SolanaAcceptTensorOfferParams) => {
        const from = solanaAddressAtom()
        const container = sdkContainerAtom()

        if (!from) {
          throw new Error(
            "Solana wallet address is not available to accept an offer",
          )
        }
        if (!container) {
          throw new Error("Solana container is not initialized")
        }

        const response = await wrap(
          bffApiClient.api.nfts.tensor.sell.$post({
            json: {
              seller: from,
              itemId: nft.id,
              collectionId: nft.collection.id,
              type: offer.type,
              bidAddress: offer.bidAddress,
              expectedPrice: offer.price,
            },
          }),
        )
        if (!response.ok) {
          throw await wrap(readExternalTradeError(response, "Tensor"))
        }

        const { txV0 } = await wrap(response.json())
        const txId = await wrap(
          sendExternalSolanaTransaction({
            connection: container.resolve(SolanaTokens.CONNECTION_ATOM)(),
            signer: container.resolve(SolanaTokens.SIGNER_ATOM)(),
            from,
            bytes: txV0.data,
          }),
        )
        return { txId }
      },
      "solanaAcceptTensorOfferAction",
    ).extend(
      withAsync(),
      withCallHook(() => {
        sonnerToast.custom(
          (id) => (
            <AppToast.Pending
              id={id}
              title="Accept offer"
              main={<Trans>Accept offer</Trans>}
              second={<Trans>Pending...</Trans>}
            />
          ),
          { duration: Number.POSITIVE_INFINITY },
        )
      }),
    )

    acceptAction.onReject.extend(
      withCallHook(({ error }) => {
        const message = getTransactionErrorMessage(error)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Error
            id={id}
            title="Accept offer"
            main={<Trans>Accepting the offer failed</Trans>}
            second={
              <div className="font-semibold text-destructive">{message}</div>
            }
          />
        ))
      }),
    )

    acceptAction.onFulfill.extend(
      withCallHook(({ payload, params: [params] }) => {
        invalidateNftOffersQueries(params.nft.id)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <AppToast.Success
            id={id}
            title="Accept offer"
            main={<Trans>Offer accepted</Trans>}
            second={
              <SuccessViewTransaction
                transactionId={payload.txId}
                blockchain={Blockchain.SOLANA}
              />
            }
          />
        ))
      }),
    )

    return acceptAction
  }

  return { acceptTensorOfferActionFactory }
})
